import { Box, Button, CircularProgress, Rating, Typography, useTheme } from '@mui/material';
import React, { useRef } from 'react';
import { IZone } from '../../models';
import { DialogActions, DialogContent } from '../dialogs';
import { Dialog, DialogTitle } from '.';
import { useZoneRatingFormManager } from '../../forms/zones/zone-rating/zone-rating-form.hook';
import { toast } from 'react-toastify';

interface ZoneRatingProps {
  zone: IZone;
}

export const ZoneRating = (props: ZoneRatingProps) => {
  const theme = useTheme();
  const [title, setTitle] = React.useState('Tell Us What You Think');
  const [subtitle, setSubtitle] = React.useState('');
  const [confirmVisible, setConfirmVisible] = React.useState(false);
  const rating = useRef<number | null>(null);

  const handleChange = (value: number | null) => {
    if (value) {
      rating.current = value;
      formManager.setFieldValue('rating', value);
      setConfirmVisible(true);
    }
  };

  const handleConfirm = async () => {
    await formManager.submitForm();
  };

  const handleSubmitted = () => {
    toast.success(<Typography>Your feedback was received successfully</Typography>);
    if (rating.current !== null) {
      if (rating.current <= 2) {
        setTitle('Something not look right? Let us know!');
      } else {
        setTitle('Thank you!');
        setSubtitle('Your feedback is very important');
      }
    }
    setConfirmVisible(false);
  };

  const handleClose = () => {
    setConfirmVisible(false);
  };

  const [{ formManager, loading }] = useZoneRatingFormManager({ zoneId: props.zone.id, next: handleSubmitted });

  return (
    <Box style={{ textAlign: 'center' }}>
      <Typography style={{ fontWeight: 'bold' }}>{title}</Typography>
      {subtitle && <Typography style={{ fontWeight: 'bold' }}>{subtitle}</Typography>}
      <ConfirmationDialog
        visible={confirmVisible}
        rating={formManager.values.rating}
        onClose={handleClose}
        onConfirm={handleConfirm}
        loading={loading}
      />
      {loading ? (
        <CircularProgress color="primary" size={16} />
      ) : (
        <Rating
          sx={{ color: theme.palette.primary.main }}
          value={formManager.values.rating}
          onChange={(event, rating) => handleChange(rating)}
        />
      )}
    </Box>
  );
};

const ConfirmationDialog = (props: {
  visible: boolean;
  rating: number | null;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
}) => {
  const { visible, rating, loading = false, onClose, onConfirm } = props;
  const theme = useTheme();

  return (
    <Dialog onClose={onClose} open={visible} fullScreen={false}>
      <DialogTitle>
        <Typography variant="subtitle2" component="span">
          Do you want to submit this rating?
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ textAlign: 'center' }}>
          <Rating sx={{ color: theme.palette.primary.main }} value={rating} readOnly />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => onClose()}
          sx={{
            paddingX: 2,
            color: (theme) => theme.palette.primary.main,
          }}
        >
          No
        </Button>
        <Button variant="contained" onClick={onConfirm} disabled={loading}>
          {loading ? <CircularProgress color="primary" size={16} /> : 'Yes!'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
